import { staticPath } from "faqSrc/lib/$path";
import { css } from "styled-components";
import { breakPoint, colorImportant, colorPrimary } from "lib/constants";
import { TweetDarkTheme } from "~/styles/Theme";

// markdown CSS
export const markdownStyle = () => css`
  h2,
  h3,
  h4,
  h5 {
    font-weight: bold;
  }

  h2 {
    font-size: 26px;
    line-height: 2;
    margin: 80px 0 20px;
    position: relative;
    padding: 10px 16px 8px;
    background: var(--cont-bg-clr);

    &:first-child {
      margin-top: 32px;
    }
  }

  h3 {
    font-size: 24px;
    line-height: 1.7;
    border: 1px solid #d3d3d3;
    margin: 48px 0 24px;
    padding: 10px 16px 8px;
  }

  h4 {
    border-left: 4px solid var(--bdr-clr);
    color: var(--title-clr);
    font-size: 22px;
    padding-left: 12px;
    margin: 48px 0 24px;
  }

  h5 {
    font-size: 18px;
    margin-top: 24px;
    text-decoration: underline var(--bdr-clr) solid 2px;
    text-underline-offset: 2px;
  }

  a {
    color: var(--body-clr);
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  code {
    background: var(--sep-outline-bdr-clr);
    border-radius: 4px;
    font-family: SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace,
      Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
    font-size: 0.85em;
    margin: 0 2px 0 2px;
    padding: 0.2em 0.6em;
    vertical-align: 0.08em;
  }

  img {
    border: 1px solid var(--cont-bg-clr);
    display: block;
    margin: 8px auto;
    max-height: 768px;
    padding: 0;
  }

  amp-img {
    margin: 0 auto;
  }

  img,
  .amp-image {
    ~ em {
      color: var(--cap-clr);
      display: block;
      font-size: 0.92em;
      line-height: 1.2;
      margin: 16px auto 0;
      position: relative;
      text-align: center;

      a {
        color: currentColor;
      }
    }
    ~ br + em {
      margin: -16px auto 0;
    }
  }

  p {
    margin: 12px 0 40px;
  }
  section > p {
    > img {
      margin: 36px auto;
      // keep 24px space on each side
      max-width: calc(100% - 48px);
      + br {
        // eliminate the br space after image
        display: none;
      }
    }
  }
  section > .amp-image {
    margin: 36px auto;
    // keep 24px space on each side
    max-width: calc(100% - 48px);
    + br {
      // eliminate the br space after image
      display: none;
    }
  }

  ol {
    list-style: decimal;
    margin: 16px 0 16px 24px;
  }
  ul {
    list-style: disc;
    margin: 16px 0 16px 24px;
  }
  li {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    border-bottom: solid 3px ${colorPrimary[300]};
  }

  strong.bold-red {
    border-bottom: solid 3px ${colorImportant[300]};
  }

  .scrollTableContainer {
    margin: 28px 0;
    overflow-x: auto;
  }

  ${tableStyles};

  table {
    margin: 0 auto 32px auto;

    amp-img {
      // reset image style for img inside amp-img of table
      img {
        max-height: unset;
        max-width: 100%;
        padding: 0;
      }
    }

    &.full-width {
      width: 100%;
    }
  }

  th,
  td {
    min-width: 160px;
    vertical-align: top;
    background-clip: padding-box;
  }

  td.md__video-column {
    width: 100%;
  }

  blockquote {
    border-left: 3px solid #b3bfc7;
    color: var(--cap-clr);
    font-size: 0.97em;
    margin: 28px 0;
    padding-left: 16px;

    & > :last-child {
      margin-bottom: 0;
    }
  }

  amp-youtube {
    margin-top: 16px;
  }

  .md__youtube {
    position: relative;
    width: 100%;
    padding-top: 56.25%;

    iframe {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
    }
  }

  .md__LinkArticle {
    a {
      text-decoration: none;

      &:hover .md__LinkArticle__Content,
      &:active .md__LinkArticle__Content {
        background-color: var(--hv-box-bg-clr);
      }

      &:hover .md__LinkArticle__title,
      &:active .md__LinkArticle__title {
        text-decoration: underline;
      }
    }

    amp-img {
      margin: 0 auto;
      img {
        cursor: pointer;
      }
    }

    &__Content {
      align-items: stretch;
      background-color: var(--body-bg-clr);
      border-radius: 4px;
      border: 1px solid var(--sep-outline-bdr-clr);
      display: flex;
      gap: 12px;
      justify-content: space-between;
      margin: 16px 0;
      padding: 6px 12px;

      .mainContainer {
        align-items: stretch;
        display: grid;
        overflow-y: hidden;
        padding: 6px 0;

        .md__LinkArticle__title {
          color: var(--title-clr);
          font-size: 1em;
          font-weight: bold;
          line-height: 1.5em;
          max-height: 3.05em;
          overflow: hidden;
          text-overflow: ellipsis;
          user-select: none;
          word-break: break-word;
        }

        .md__LinkArticle__description {
          color: var(--body-clr);
          font-size: 0.8em;
          font-weight: normal;
          margin-top: 4px;
          max-height: 1.8em;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .md__LinkArticle__publisher {
          align-items: center;
          color: var(--body-clr);
          display: grid;
          font-size: 15px;
          font-weight: normal;
          gap: 8px;
          grid-template-columns: 18px auto;
          line-height: 18px;
          margin-top: auto;
          overflow: hidden;
          padding-top: 12px;
          text-overflow: ellipsis;
          white-space: nowrap;

          img {
            width: 18px;
            margin: 0;
            padding: 0;
          }
        }
      }

      .imageContainer {
        align-self: center;
        flex-shrink: 0;
        height: 120px;
        width: 120px;
        img {
          height: 100%;
          margin: auto;
          object-fit: cover;
          padding: 0;
          text-align: center;
          width: 100%;
        }
      }

      &__NoImage {
        .imageContainer {
          background: url(${staticPath.img.web_site_svg}) center right no-repeat;
          background-size: contain;
        }
      }
    }
  }

  .md__Notification {
    -webkit-box-align: center;
    align-items: center;
    -webkit-box-pack: justify;
    justify-content: space-between;
    padding: 1em 1.2em;
    background-color: #f7f9f9;
    border-radius: 4px;
    margin-top: 40px;
    color: #787c7b;
    word-break: break-all;

    p {
      margin: 0;
    }

    .md__Notification__Content {
      font-size: 0.8em;
      margin: 8px 0 0;
    }
  }

  .md__alert {
    background-position: 16px center;
    background-repeat: no-repeat;
    background-size: 24px 24px;
    border-radius: 5px;
    border: none;
    color: var(--body-clr);
    margin-bottom: 24px;
    padding: 16px 16px 16px 56px;

    > p {
      margin: 8px 0;
    }
    > p:first-child:empty,
    > p:last-child:empty {
      margin: 0;
    }
    > ul:first-child,
    > ol:first-child,
    > p:first-child,
    > p:first-child:empty + ul,
    > p:first-child:empty + ol {
      margin-top: 0;
    }
    > ul:last-child,
    > ol:last-child,
    > p:last-child {
      margin-bottom: 0;
    }

    strong {
      border-bottom: none;
      padding: 0;
      margin: 0;
    }

    &.md__alertHint {
      background-color: var(--alert-hint);
      background-image: url(${staticPath.img.icon_idea_svg});
    }

    &.md__alertCaution {
      background-color: var(--alert-caution);
      background-image: url(${staticPath.img.icon_warning_svg});
    }

    // alert-danger is deprecated and no longer exists in editor toolbar.
    &.md__alertDanger {
      background-color: var(--alert-danger);
      background-image: url(${staticPath.img.icon_exclamation_svg});
    }

    &.md__alertTips {
      background-color: var(--body-bg-clr);
      border: 1px dashed var(--alert-tip);
      padding: 16px;
    }
  }

  .md__SingleComment {
    display: grid;
    grid-template-columns: 48px 1fr;
    gap: 20px;
    margin: 24px 0;
    img {
      margin: 0; // reset inherit style from parent
    }
    > a {
      text-decoration: unset;
    }
    > .author {
      background: url(${staticPath.img.icon_user_placeholder_svg}) no-repeat
        center center;
      background-size: contain;
      height: 48px;
      margin-bottom: 32px;
      position: relative;
      width: 48px;
      &:after {
        background-color: var(--cont-bg-clr);
        content: "";
        height: 32px;
        margin-top: 4px;
        position: absolute;
        top: 48px;
        width: 100%;
      }
    }
    > .comment {
      align-self: start;
      background: var(--md-cmt-bg-clr);
      border: 2px solid var(--box-bdr-clr);
      border-radius: 5px;
      padding: 16px;
      position: relative;

      &:after,
      &:before {
        content: "";
        border-bottom: 14px solid transparent;
        border-top: 2px solid transparent;
        height: 0;
        position: absolute;
        width: 0;
        z-index: 1;
      }

      &:after {
        top: 18px;
        border-right: 16px solid var(--md-cmt-bg-clr);
        left: -10px;
      }
      &:before {
        top: 16px;
        border-right: 16px solid var(--box-bdr-clr);
        left: -16px;
      }

      > p:last-child,
      > p:empty {
        margin: 0;
      }
    }
  }

  .is-lightbox {
    cursor: zoom-in;
  }

  .text-center {
    text-align: center;
  }

  ${highlightStyles()};

  ${tweetStyles()};

  @media only screen and (max-width: ${breakPoint["sm"]}) {
    ${spStyles()};
  }
`;

const tweetStyles = () => css`
  .static-tweet {
    --colors-blue: #0c00ff;
    --colors-purple: #be00ff;

    --accents-1: #fafafa;
    --accents-2: #eaeaea;
    --accents-3: #999999;
    --accents-4: #888888;
    --accents-5: #666666;

    --bg-color: var(--body-bg-clr);
    --link-color: var(--colors-blue);
    --poll-bar-color: var(--colors-blue);
    --inline-code-color: var(--colors-purple);
    --code-color: #9efeff;
    --code-bg-color: #1e1e3f;

    --text-margin: 1.25rem 0;
    --container-margin: 1.5rem 0;
    --poll-margin: 1.5rem 1rem;
    --heading-margin-top: 3.5rem;
    --heading-margin-bottom: 2rem;
    --li-margin: 0 0 0.5rem 0;

    /* Embedded tweet */
    --tweet-font: normal normal 16px/30px inherit;
    --tweet-f-clr: #1c2022;
    --tweet-bg-clr: #fff;
    --tweet-bdr: 1px solid #e1e8ed;
    --tweet-bdr-hv: 1px solid #ccd6dd;
    --tweet-avatar-border: none;
    --tweet-link-color: #2b7bb9;
    --tweet-link-color-hover: #3b94d9;
    --tweet-clr-b: black;
    --tweet-color-gray: #697882;
    --tweet-color-red: #e02460;

    @media only screen and (min-width: ${breakPoint["md"]}) {
      --tweet-font: normal normal 18px/36px inherit;
    }
    ${(props: { theme?: { supportDarkMode?: boolean } }) => {
      if (props.theme?.supportDarkMode) {
        return TweetDarkTheme;
      }
    }}
  }

  .static-tweet {
    width: 100%;
    max-width: 720px;
    min-width: 220px;
    margin-left: auto;
    margin-right: auto;
  }

  .static-tweet-caption {
    font-size: 14px;
    color: #999;
    text-align: center;
    margin: 0;
    margin-top: 10px;
    padding: 0;
  }

  .static-tweet-anchor,
  .static-tweet-twitter-link {
    color: var(--tweet-link-color);
    text-decoration: none;
  }

  @media (any-hover: hover) {
    .static-tweet-anchor:hover,
    .static-tweet-twitter-link:hover {
      text-decoration: underline;
    }
  }

  .static-tweet code {
    font-size: 14px;
    font-family: Menlo, Monaco, Lucida Console, Liberation Mono,
      DejaVu Sans Mono, Bitstream Vera Sans Mono, Courier New, monospace, serif;
  }

  .static-tweet code.inline {
    color: var(--inline-code-color);
    font-size: 1rem;
    white-space: pre-wrap;
  }

  .static-tweet pre {
    color: var(--code-color);
    background: var(--code-bg-color);
    padding: 1.25rem;
    margin: var(--container-margin);
    white-space: pre;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  .static-tweet .image-container {
    border: var(--tweet-bdr);
    border-radius: 16px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(50%, 1fr));
    margin: var(--container-margin);
    overflow: hidden;
  }
  .static-tweet .image-count-3 > :first-child {
    grid-row-end: span 2;
  }
  .static-tweet .image-count-4 {
    border: none;
    gap: 0.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .static-tweet .gif-container,
  .static-tweet .video-container {
    background-color: #000;
    border: var(--tweet-bdr);
    border-radius: 16px;
    margin: var(--container-margin);
    overflow: hidden;

    video,
    amp-video {
      max-height: 500px;
      width: 100%;
    }
  }

  .static-tweet-permalink span[id] {
    display: block;
    position: absolute;
    visibility: hidden;
    margin-top: calc(-1 * var(--heading-margin-top));
    padding-top: var(--heading-margin-top);
  }

  .static-tweet-permalink a {
    color: inherit;
    text-decoration: none;
  }
  @media (any-hover: hover) {
    .static-tweet-permalink a:hover {
      color: inherit;
      border-bottom: 1px solid;
    }
    .static-tweet-permalink a:hover ~ .permalink {
      visibility: visible;
    }
  }
  .static-tweet-permalink .permalink {
    visibility: hidden;
    display: none;
    font-weight: 500;
  }
  @media screen and (min-width: 992px) {
    .static-tweet-permalink a {
      margin-right: 0.5rem;
    }
    .static-tweet-permalink .permalink {
      display: inline-block;
    }
  }

  .static-tweet-h1,
  .static-tweet-h2,
  .static-tweet-h3,
  .static-tweet-h4,
  .static-tweet-h5,
  .static-tweet-h6 {
    font-weight: 600;
    margin: var(--heading-margin-top) 0 var(--heading-margin-bottom) 0;
  }

  .static-tweet-h1 {
    font-size: 2rem;
  }

  .static-tweet-h2 {
    font-size: 1.75rem;
  }

  .static-tweet-h3 {
    font-size: 1.5rem;
  }

  .static-tweet-h4 {
    font-size: 1.25rem;
  }

  .static-tweet-h5 {
    font-size: 1rem;
  }

  .static-tweet-h6 {
    font-size: 0.875rem;
  }

  .static-tweet ul {
    margin: var(--text-margin);
    list-style-type: none;
    padding-left: 1rem;
  }

  .static-tweet ul li:before {
    content: "-";
    color: var(--accents-3);
    position: absolute;
    margin-left: -1rem;
  }

  .static-tweet ol {
    margin: var(--text-margin);
    padding-left: 1rem;
  }

  .static-tweet li {
    padding-left: 0;
    margin: var(--li-margin);
  }

  .static-tweet-summary {
    position: relative;
    box-sizing: border-box;
  }

  .static-tweet-details {
    height: 100%;
    overflow: hidden;
  }

  .static-tweet-summary {
    position: relative;
    height: 100%;
    list-style: none;
  }

  .static-tweet-summary::marker {
    display: none;
  }

  .static-tweet-summary img,
  .static-tweet-summary amp-img {
    border-radius: 1rem;
    padding: 0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    object-fit: cover;
    max-width: 100%;
    max-height: 100%;
    margin: 0;
    height: 100%;
    width: 100%;
  }

  .static-tweet-table-container {
    display: flex;
    justify-content: center;
    width: 100%;
    margin: var(--container-margin);
  }

  .static-tweet-table-container table {
    display: block;
    overflow: auto;
    border-collapse: collapse;
  }

  .static-tweet-table-container th {
    font-weight: 600;
    padding: 0.5rem 0.875rem;
    border: 1px solid var(--accents-2);
  }

  .static-tweet-table-container td {
    padding: 0.5rem 0.875rem;
    border: 1px solid var(--accents-2);
  }

  .static-tweet-p {
    margin: var(--text-margin);
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.8;
  }

  .static-tweet blockquote {
    border: none;
    color: var(--tweet-f-clr);
    font-size: inherit;
    margin-block-end: 0;
    margin-block-start: 0;
    margin-inline-end: 0;
    margin-inline-start: 0;
    margin: 0;
  }

  .static-tweet-blockquote {
    background: var(--accents-1);
    color: var(--accents-5);
    border: 1px solid var(--accents-2);
    margin: var(--container-margin);
    padding: 0 1.25rem;
  }

  .static-tweet-hr {
    border: 0;
    border-top: 1px solid var(--accents-2);
    margin: var(--text-margin);
  }

  .static-tweet-twitter-link,
  .static-tweet-twitter-link s {
    text-decoration: none;
  }

  @media (any-hover: hover) {
    .static-tweet-twitter-link:hover {
      text-decoration: underline;
    }
  }

  .static-tweet-emoji {
    height: 1.2em;
    width: 1.2em;
    margin: 0 2px;
    vertical-align: -3px;
    max-width: 100%;
    padding: 0;
    display: inline;
    pointer-events: none;
  }

  .static-tweet-poll {
    margin: var(--poll-margin);
  }
  .static-tweet-options {
    display: grid;
    grid-template-columns: max-content 14rem max-content;
    align-items: center;
    grid-gap: 1rem;
    overflow: auto;
  }
  .static-tweet-label {
    overflow: auto;
    text-align: right;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .static-tweet-chart {
    height: 100%;
    background: var(--poll-bar-color);
  }
  .static-tweet-poll hr {
    border: 0;
    border-top: 1px solid var(--accents-2);
    margin: 1rem 0 0.5rem 0;
  }
  .static-tweet-footer {
    display: flex;
    font-size: 0.875rem;
    color: var(--accents-4);
  }
  .static-tweet-votes-count {
    flex-grow: 1;
  }
  @media screen and (max-width: 450px) {
    .static-tweet-options {
      grid-template-columns: max-content 7rem max-content;
    }
  }

  .static-tweet-info a {
    text-decoration: none;
  }
  .static-tweet-info {
    font-size: 0.875rem;
    display: flex;
  }
  .static-tweet-like {
    display: flex;
    color: var(--tweet-color-gray);
    margin-right: 0.75rem;
  }
  .static-tweet-like:visited {
    color: var(--tweet-link-color);
  }
  @media (any-hover: hover) {
    .static-tweet-like:hover {
      color: var(--tweet-color-red);
    }
    .static-tweet-like:hover .static-tweet-icon-heart {
      background-image: url(${staticPath.img.static_tweet
        .icon_heart_hover_svg});
    }
  }
  .static-tweet-icon-heart {
    width: 1.25em;
    background-image: url(${staticPath.img.static_tweet.icon_heart_svg});
  }
  .static-tweet-likes {
    margin-left: 0.25rem;
  }
  .static-tweet-time {
    display: block;
    flex: 1;
    color: var(--tweet-color-gray);
  }
  @media (any-hover: hover) {
    .static-tweet-time:hover,
    .static-tweet-time:focus {
      color: var(--tweet-link-color-hover);
    }
    .static-tweet-time:focus {
      text-decoration: underline;
    }
  }

  .static-tweet-body {
    color: var(--tweet-f-clr);
    font: var(--tweet-font);
    overflow: hidden;
    background: var(--tweet-bg-clr);
    border: var(--tweet-bdr);
    border-radius: 5px;
    margin: var(--container-margin);
  }

  @media (any-hover: hover) {
    .static-tweet-body:hover {
      border: var(--tweet-bdr-hv);
    }
  }

  .static-tweet-body-blockquote {
    position: relative;
    padding: 1.25rem 1.25rem 0.625rem 1.25rem;
  }

  .static-tweet-icon {
    display: inline-block;
    height: 1.25em;
    vertical-align: text-bottom;
    background-size: contain;
    background-repeat: no-repeat;
  }

  .static-tweet-header {
    display: flex;
  }
  .static-tweet-header-avatar {
    height: 2.25rem;
    width: 2.25rem;
    margin-right: 0.625rem;
    pointer-events: none;
  }
  .static-tweet-header-author {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
  }
  @media (any-hover: hover) {
    .static-tweet-header-author:hover {
      color: var(--tweet-link-color-hover);
    }
  }
  .static-tweet-header-name,
  .static-tweet-header-username {
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    color: var(--tweet-clr-b);
  }
  .static-tweet-header-name {
    font-weight: 700;
  }
  .static-tweet-header-username {
    color: var(--tweet-color-gray);
    font-size: 0.875rem;
  }
  .static-tweet-header-brand {
    margin-left: auto;
  }
  .static-tweet-header-icon-twitter {
    display: inline-block;
    height: 1.25em;
    vertical-align: text-bottom;
    background-size: contain;
    background-repeat: no-repeat;
    width: 1.25em;
    background-image: url(${staticPath.img.static_tweet.icon_twitter_svg});
  }
  .static-tweet-header-rounded {
    border-radius: 50%;
    padding: 0;
    max-width: 100%;
  }

  .static-tweet-header-avatar amp-img {
    border-radius: 50%;
    margin: 0;
  }

  .static-tweet-conversation {
    display: flex;
    color: var(--tweet-link-color);
    font-size: 0.875rem;
    align-items: center;
    padding: 0.625rem 1.25rem;
    text-decoration: none;
    border-top: var(--tweet-bdr);
  }
  .static-tweet-conversation:hover {
    color: var(--tweet-link-color-hover);
  }
  .static-tweet-conversation:hover > .static-tweet-icon-reply {
    background-image: url(${staticPath.img.static_tweet.icon_reply_hover_svg});
  }
  .static-tweet-conversation .static-tweet-text {
    margin-left: 0.25rem;
  }
  .static-tweet-icon-reply {
    width: 1.25em;
    background-image: url(${staticPath.img.static_tweet.icon_reply_svg});
  }
  .static-tweet-icon-profile {
    width: 1.25em;
    background-image: url(${staticPath.img.static_tweet.icon_profile_svg});
  }
  .static-tweet-icon-chevron {
    width: 1.25em;
    background-image: url(${staticPath.img.static_tweet.icon_chevron_svg});
    margin-left: auto;
  }
  .static-tweet-preview-link {
    border: var(--tweet-bdr);
    border-radius: 16px;
    display: block;
    margin-top: 12px;
    overflow: hidden;
    text-decoration: none;
    white-space: normal;
    span {
      color: var(--tweet-color-gray);
      display: block;
      font-size: 15px;
      overflow: hidden;
    }
    > .preview-image {
      background: #fff;
      > img {
        height: auto;
        max-width: fit-content;
        width: 100%;
        padding: 0;
      }
    }
    > .preview-content {
      background: var(--tweet-bg-clr);
      border-top: var(--tweet-bdr);
      padding: 12px;
      > .desc {
        display: -webkit-box;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
      > .title {
        color: var(--tweet-clr-b);
        font-weight: 700;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
`;

export const tableStyles = () => css`
  table {
    border-collapse: collapse;
    background-color: var(--body-bg-clr);
    color: var(--body-clr);
    text-align: center;

    th {
      background-color: var(--imp-clr10);
      vertical-align: top;
    }

    th,
    td {
      text-align: left;
      padding: 12px 12px 12px 24px;
      border: 2px solid var(--imp-clr10);
      > p {
        margin: 0;
      }
    }

    ul,
    ol,
    dl {
      margin: 12px 0 12px 24px;
      text-align: left;
    }

    amp-img,
    img {
      margin: 0;
    }
  }
`;

// highlight theme style
// supported theme can be found at node_modules\highlight.js\styles
const highlightStyles = () => css`
  /*
  * Visual Studio 2015 dark style
  */
  .hljs {
    display: block;
    overflow-x: auto;
    padding: 0.5em;
    background: #1E1E1E;
    color: #DCDCDC;
  }

  .hljs-keyword,
  .hljs-literal,
  .hljs-symbol,
  .hljs-name {
    color: #569CD6;
  }
  .hljs-link {
    color: #569CD6;
    text-decoration: underline;
  }

  .hljs-built_in,
  .hljs-type {
    color: #4EC9B0;
  }

  .hljs-number,
  .hljs-class {
    color: #B8D7A3;
  }

  .hljs-string,
  .hljs-meta-string {
    color: #d69d85;
  }

  .hljs-regexp,
  .hljs-template-tag {
    color: #9A5334;~
  }

  .hljs-subst,
  .hljs-function,
  .hljs-title,
  .hljs-params,
  .hljs-formula {
    color: #DCDCDC;
  }

  .hljs-comment,
  .hljs-quote {
    color: #57A64A;
    font-style: italic;
  }

  .hljs-doctag {
    color: #608B4E;
  }

  .hljs-meta,
  .hljs-meta-keyword,
  .hljs-tag {
    color: #9B9B9B;
  }

  .hljs-variable,
  .hljs-template-variable {
    color: #BD63C5;
  }

  .hljs-attr,
  .hljs-attribute,
  .hljs-builtin-name {
    color: #9CDCFE;
  }

  .hljs-section {
    color: gold;
  }

  .hljs-emphasis {
    font-style: italic;
  }

  .hljs-strong {
    font-weight: bold;
  }

  .hljs-bullet,
  .hljs-selector-tag,
  .hljs-selector-id,
  .hljs-selector-class,
  .hljs-selector-attr,
  .hljs-selector-pseudo {
    color: #D7BA7D;
  }

  .hljs-addition {
    background-color: #144212;
    display: inline-block;
    width: 100%;
  }

  .hljs-deletion {
    background-color: #600;
    display: inline-block;
    width: 100%;
  }
`;

// style for smart phone
export const spStyles = () => css`
  h2 {
    font-size: 20px;
    line-height: 1.8;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 17px;
  }
  h5 {
    font-size: 16px;
  }
  img {
    max-width: 100%;
  }
  // keep 12px space on each side
  section > p > img,
  section > .amp-image {
    max-width: calc(100% - 24px);
  }
  table {
    th,
    td {
      min-width: 100px;
      padding: 12px 12px 12px 16px;
    }
    ul,
    ol,
    dl {
      margin: 12px 0 12px 16px;
    }
  }
  .md__LinkArticle__Content {
    .mainContainer .md__LinkArticle__description {
      margin: 0;
    }
    .imageContainer {
      height: 88px;
      width: 88px;
    }
  }
  .md__SingleComment {
    grid-template-columns: 40px 1fr;
    gap: 16px;
    > .author {
      height: 40px;
      margin-bottom: 24px;
      width: 40px;
      &:after {
        height: 24px;
        top: 40px;
      }
    }
  }
`;
